import { createHttpClient } from '@adapters/HttpClient';
import { Spinner, usePageNavigation } from '@shared/components';
import { useToast } from '@shared/hooks/use-toast';
import { useQueryParams } from '@shared/hooks/useQueryParams';
import { usePermissions } from '@shared/hooks/usePermissions';
import { apiClientWH } from '@shared/services';
import { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RolesApiAdapter } from '../../adapters/RolesApiAdapter';
import { useRoles } from '../../hooks/useRoles';
import { useUpdateRole } from '../../hooks/useUpdateRole';
import { RolesModel } from '../../models/Roles';
import { RolesView } from '../display/RolesView';
import { ErrorState } from '../feedback';
import { DeleteModal, DeleteModalRef } from '../modals/DeleteModal';
import { CreateModal, CreateModalRef } from '../modals/CreateModal';

const URL_SYNC_DEBOUNCE_MS = 500;

function filterRoles(roles: RolesModel[], search: string) {
  if (!roles.length) return [];

  const searchLower = search.toLowerCase().trim();
  if (!searchLower) return roles;

  return roles.filter((role) => role.name.toLowerCase().includes(searchLower));
}

export const Main = () => {
  const { t } = useTranslation('rolesModule');
  const { isAdminRole } = usePermissions();
  const deleteModalRef = useRef<DeleteModalRef>(null);
  const editModalRef = useRef<CreateModalRef>(null);
  const [roleToDelete, setRoleToDelete] = useState<RolesModel | null>(null);
  const { toast } = useToast();
  const updateRoleMutation = useUpdateRole();

  const { params: queryParams, updateParams: updateQueryParams } = useQueryParams({
    search: { type: 'string', default: '' },
  });
  const [searchTerm, setSearchTerm] = useState(() => queryParams.search ?? '');
  const deferredSearch = useDeferredValue(searchTerm);
  const urlSyncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    roles: allRoles,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    total,
  } = useRoles({ sortBy: 'id', sortDirection: 'desc' });

  const filteredRoles = useMemo(() => filterRoles(allRoles, deferredSearch), [allRoles, deferredSearch]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
  }, []);

  useEffect(() => {
    if (urlSyncTimerRef.current) {
      clearTimeout(urlSyncTimerRef.current);
    }

    urlSyncTimerRef.current = setTimeout(() => {
      const urlSearch = queryParams.search ?? '';
      if (urlSearch !== searchTerm) {
        updateQueryParams({ search: searchTerm });
      }
    }, URL_SYNC_DEBOUNCE_MS);

    return () => {
      if (urlSyncTimerRef.current) {
        clearTimeout(urlSyncTimerRef.current);
      }
    };
  }, [searchTerm, queryParams.search, updateQueryParams]);

  const handleDelete = useCallback(
    (role: RolesModel) => {
      setRoleToDelete(role);
      deleteModalRef.current?.open(async (replaceRoleId) => {
        const httpClient = createHttpClient(apiClientWH);
        const apiAdapter = new RolesApiAdapter(httpClient);
        try {
          await apiAdapter.deleteRole(role.id, replaceRoleId);
          refetch();
          toast({ title: t('toast.deleteSuccess') });
        } catch (e: unknown) {
          const err = e as {
            response?: { data?: { message?: string }; config?: { url?: string } };
            message?: string;
            config?: { url?: string };
          };
          const apiMsg = err?.response?.data?.message || err?.message || t('toast.deleteError');
          const url = err?.config?.url || err?.response?.config?.url || '';
          toast({
            title: t('toast.deleteError'),
            description: `${apiMsg}${url ? `\nURL: ${url}` : ''}`,
            variant: 'destructive',
          });
        }
      });
    },
    [refetch, toast, t],
  );

  const handleEdit = useCallback(
    (role: RolesModel) => {
      if (!editModalRef.current?.open) return;

      const permissionSlugs = role.permissions.map((perm) => perm.slug);

      editModalRef.current.open({
        mode: 'edit',
        initialName: role.name,
        initialPermissions: permissionSlugs,
        onSubmit: async (name, permissions) => {
          await updateRoleMutation.mutateAsync({
            roleId: role.id,
            name,
            permissions,
          });
        },
      });
    },
    [updateRoleMutation],
  );

  const breadcrumbs = useMemo(() => [{ label: t('pageTitle') }], [t]);

  usePageNavigation({ breadcrumbs });

  if (!isAdminRole()) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-16" data-testid="roles-loading">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full py-16">
        <ErrorState error={error as Error} onRetry={refetch} className="h-auto" />
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <DeleteModal ref={deleteModalRef} roles={allRoles} roleToDelete={roleToDelete?.id ?? ''} />
      <CreateModal ref={editModalRef} />

      <RolesView
        roles={filteredRoles}
        totalCount={total}
        searchTerm={searchTerm}
        isFetching={isFetching}
        isFiltering={searchTerm !== deferredSearch}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        onSearchChange={handleSearchChange}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onRefetch={refetch}
      />
    </div>
  );
};
